
import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form, Input, Button, Select, DatePicker, TimePicker, message } from 'antd';
import { ArrowLeftOutlined, CalendarOutlined, ClockCircleOutlined, DownOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { eventApi, type CreateEventRequest, type UpdateEventRequest } from '../../services/events';
import { hackathonApi } from '../../services/hackathon';
import ImageUploader from '../../components/common/ImageUploader';

const { TextArea } = Input;
const { Option } = Select;

const EventForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [activeHackathonId, setActiveHackathonId] = useState<string | null>(null);
  const [imageGallery, setImageGallery] = useState<{ id: string; value: string }[]>([]);

  const isEditMode = !!id;

  // Fetch all hackathons to find the active one
  const { data: hackathonsResponse } = useQuery({
    queryKey: ['hackathons'],
    queryFn: hackathonApi.getHackathons,
  });

  // Find active hackathon
  useEffect(() => {
    if (hackathonsResponse?.data) {
      const activeHackathon = hackathonsResponse.data.find(h => h.isActive === true);
      if (activeHackathon) {
        setActiveHackathonId(activeHackathon._id);
      }
    }
  }, [hackathonsResponse]);

  // Fetch event data for editing
  const { data: eventResponse, isLoading: isLoadingEvent, error: eventError } = useQuery({
    queryKey: ['event', id],
    queryFn: () => eventApi.getEventById(id!),
    enabled: isEditMode && !!id,
  });

 

  // Set form values when editing
  useEffect(() => {
    if (isEditMode && eventResponse?.data) {
      const event = eventResponse.data;
   
      
      // Parse date - try multiple formats
      let parsedDate = null;
      if (event.date) {
        // Try DD/MM/YYYY format first
        parsedDate = dayjs(event.date, 'DD/MM/YYYY');
        if (!parsedDate.isValid()) {
          // Try other common formats
          parsedDate = dayjs(event.date);
        }
        if (!parsedDate.isValid()) {
          parsedDate = null;
        }
      }

      // Parse time - try multiple formats
      let parsedTime = null;
      if (event.time) {
        // Try hh:mm A format first
        parsedTime = dayjs(event.time, 'hh:mm A');
        if (!parsedTime.isValid()) {
          // Try other common formats
          parsedTime = dayjs(event.time, 'HH:mm');
        }
        if (!parsedTime.isValid()) {
          parsedTime = null;
        }
      }

      // Reset form first to clear any previous values
      form.resetFields();
      
      // Set new values
      form.setFieldsValue({
        title: event.title,
        description: event.description,
        eventType: event.eventType,
        date: parsedDate,
        time: parsedTime,
        eventLink: event.eventLink,
      });
      
      if (event.image) {
        setImageGallery([{ id: Date.now().toString(), value: event.image }]);
      }
    }
  }, [isEditMode, eventResponse, form]);

  // Create event mutation
  const createEventMutation = useMutation({
    mutationFn: eventApi.createEvent,
    onSuccess: () => {
      message.success('Event created successfully');
      queryClient.invalidateQueries({ queryKey: ['events', activeHackathonId] });
      navigate('/events');
    },
    onError: () => {
      message.error('Failed to create event');
    },
  });

  // Update event mutation
  const updateEventMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEventRequest }) =>
      eventApi.updateEvent(id, data),
    onSuccess: () => {
      message.success('Event updated successfully');
      queryClient.invalidateQueries({ queryKey: ['events', activeHackathonId] });
      navigate('/events');
    },
    onError: () => {
      message.error('Failed to update event');
    },
  });

  const onFinish = (values: any) => {
    if (!activeHackathonId) {
      message.error('No active hackathon found');
      return;
    }

    const eventData = {
      hackathonId: activeHackathonId,
      title: values.title,
      description: values.description,
      eventType: values.eventType,
      date: values.date ? values.date.format('DD/MM/YYYY') : '',
      time: values.time ? values.time.format('hh:mm A') : '',
      eventLink: values.eventLink || '',
      image: imageGallery[0]?.value || '',
    };

    if (isEditMode) {
      updateEventMutation.mutate({ id: id!, data: eventData });
    } else {
      createEventMutation.mutate(eventData as CreateEventRequest);
    }
  };

  const isLoading = createEventMutation.isPending || updateEventMutation.isPending || isLoadingEvent;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Back Button */}
      <div className="mb-6">
        <Button 
          type="text" 
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          className="text-gray-600 hover:text-gray-900 p-2 h-auto font-medium"
        >
          Back
        </Button>
      </div>

      {/* Form Card */}
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-8">
            {isEditMode ? 'Edit Event' : 'Add Event'}
            {isLoadingEvent && <span className="text-sm text-gray-500 ml-2">(Loading...)</span>}
          </h1>
          
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            className=""
            disabled={isLoadingEvent}
          >
            {/* Event Title */}
            <Form.Item
              label={<span className="text-sm font-medium text-gray-700">Event Title*</span>}
              name="title"
              rules={[{ required: true, message: 'Please enter event title' }]}
              required={false}
            >
              <Input 
                placeholder="Event Title *"
                className="h-9 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
              />
            </Form.Item>

            {/* Description */}
            <Form.Item
              label={<span className="text-sm font-medium text-gray-700">Description*</span>}
              name="description"
              rules={[{ required: true, message: 'Please enter description' }]}
              required={false}
            >
              <TextArea 
                placeholder="Event Description *"
                rows={4}
                className="rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
              />
            </Form.Item>

            {/* Date and Time Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Date */}
              <Form.Item
                label={<span className="text-sm font-medium text-gray-700">Date*</span>}
                name="date"
                rules={[{ required: true, message: 'Please select date' }]}
                required={false}
              >
                <DatePicker
                  placeholder="DD/MM/YYYY"
                  format="DD/MM/YYYY"
                  className="w-full h-9 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
                  suffixIcon={<CalendarOutlined className="text-gray-400" />}
                />
              </Form.Item>

              {/* Time */}
              <Form.Item
                label={<span className="text-sm font-medium text-gray-700">Time*</span>}
                name="time"
                rules={[{ required: true, message: 'Please select time' }]}
                required={false}
              >
                <TimePicker
                  format="hh:mm A"
                  className="w-full h-9 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
                  suffixIcon={<ClockCircleOutlined className="text-gray-400" />}
                />
              </Form.Item>
            </div>

            {/* Event Type */}
            <Form.Item
              label={<span className="text-sm font-medium text-gray-700">Event Type*</span>}
              name="eventType"
              rules={[{ required: true, message: 'Please select event type' }]}
              required={false}
            >
              <Select
                placeholder="Select an event type"
                className="h-9 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
                suffixIcon={<DownOutlined className="text-gray-400" />}
              >
                <Option value="virtual">Virtual</Option>
                <Option value="physical">Physical</Option>
              </Select>
            </Form.Item>

            {/* Event Link */}
            <Form.Item
              label={<span className="text-sm font-medium text-gray-700">Event Link</span>}
              name="eventLink"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.resolve(); // Allow empty values since it's optional
                    }
                    
                    // URL validation regex
                    const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
                    
                    if (!urlRegex.test(value)) {
                      return Promise.reject(new Error('Please enter a valid URL (e.g., https://example.com)'));
                    }
                    
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input 
                placeholder="https://example.com or https://meet.google.com/abc-def-ghi"
                className="h-9 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
              />
            </Form.Item>

            {/* Image Upload */}
            <Form.Item
              label={<span className="text-sm font-medium text-gray-700">Event Image</span>}
            >
              <ImageUploader
                actionType={isEditMode ? "edit" : "add"}
                imageGallery={imageGallery}
                setImageGallery={setImageGallery}
                setIsUploadingGallery={() => {}}
                single={true}
                className="h-[130px]"
              />
            </Form.Item>

            {/* Submit Button */}
            <Form.Item className="mb-0">
              <Button 
                type="primary" 
                htmlType="submit"
                loading={isLoading}
                className="w-full h-12 bg-primary hover:bg-primary/90 border-primary hover:border-primary/90 rounded-lg font-medium text-base"
              >
                {isEditMode ? 'Update Event' : 'Create Event'}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default EventForm